import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Alert from '../components/ui/Alert';
import Spinner from '../components/ui/Spinner';
import { useTranslation, Trans } from 'react-i18next';
import PdfViewer from '../components/PdfViewer';
import PowerPointViewer from '../components/PowerPointViewer';
import SecureVideo from '../components/SecureVideo';
import { useToast } from '../context/ToastContext';
import ConfirmModal from '../components/ui/ConfirmModal';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { apiOrigin } from '../utils/apiBase';

// SVG Icons
const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const VideoCameraOffIcon = () => (
  <svg
    className="w-12 h-12 text-slate-400 dark:text-slate-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M12 18.75H4.5a2.25 2.25 0 01-2.25-2.25V9m13.5 0V6.75A2.25 2.25 0 0013.5 4.5h-6M2.25 21l19.5-19.5"
    />
  </svg>
);

const ClipboardDocumentCheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
    />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
    />
  </svg>
);

const WarningIcon = () => (
  <svg
    className="w-16 h-16 text-warning-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const FinalizeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

// ---- Video URL helpers (unchanged business logic) ----
const getVideoUrl = (url) => {
  if (!url) return null;

  // URL-uri externe (YouTube, Vimeo)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // YouTube
    if (url.includes('youtube.com/watch') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be')
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : new URLSearchParams(url.split('?')[1]).get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1` : url;
    }
    // Vimeo
    if (url.includes('vimeo.com')) {
      const id = url.split('vimeo.com/')[1]?.split('?')[0];
      return id ? `https://player.vimeo.com/video/${id}` : url;
    }
    return url;
  }

  // URL-uri locale - backend server
  const base = apiOrigin();
  return `${base}${url.startsWith('/') ? url : '/' + url}`;
};

// Fisher-Yates shuffle
const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const isEmbed = (url) =>
  url && (url.includes('youtube') || url.includes('youtu.be') || url.includes('vimeo'));

const TrainingPage = () => {
  const { t } = useTranslation();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const fromTestId = new URLSearchParams(window.location.search).get('fromTest');
  const videoRef = useRef(null);
  const progressInterval = useRef(null);

  const [training, setTraining] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  // materialsProgress: { [materialId]: { current, total, completed } } — pentru modulul curent.
  // Resetat la fiecare schimbare de modul; persistat în backend pe videoProgress[moduleId].materialsProgress.
  const [materialsProgress, setMaterialsProgress] = useState({});
  // Care material e deschis în viewer (din meniul modulului). Null = niciunul selectat.
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  // Sidebar materiale — retractabil. Default deschis.
  const [materialsSidebarOpen, setMaterialsSidebarOpen] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({}); // { questionId: answerId }
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  const [shuffledAnswers, setShuffledAnswers] = useState({}); // { questionId: [...shuffled answer ids] }
  // Local mirror of videoProgress that updates after every API save (server state lags)
  const [localProgress, setLocalProgress] = useState({});
  // Time tracking — seconds spent on current module this session
  const moduleTimeRef = useRef({}); // { moduleId: totalSecondsThisSession }
  const timeTickRef = useRef(null);
  // Tick forțat pentru cronometrul vizibil (re-render la fiecare secundă)
  const [displayElapsed, setDisplayElapsed] = useState(0);
  // State pentru modalul de ieșire din instruire — vizual, nu window.confirm.
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitLoading, setExitLoading] = useState(false);

  useEffect(() => {
    startOrResumeTraining();
    return () => {
      if (progressInterval.current) clearInterval(progressInterval.current);
      if (timeTickRef.current) clearInterval(timeTickRef.current);
    };
  }, [courseId]);

  useEffect(() => {
    setVideoError(false);
    setVideoWatched(false);
    setVideoProgress(0);
    setMaterialsProgress({});
    setSelectedMaterialId(null);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizPassed(false);
    setShuffledAnswers({});
    // Restore progress for already-completed modules
    if (training) {
      const mods = training.course?.modules || [];
      const mod = mods[currentModuleIndex];
      if (mod) {
        const vpSnap = training.videoProgress || {};
        const prog = { ...(vpSnap[mod.id] || {}), ...(localProgress[mod.id] || {}) };
        // Restaurează progresul materialelor (per material) dacă există în videoProgress
        if (prog.materialsProgress) setMaterialsProgress(prog.materialsProgress);
        // Pre-selectează primul material al modulului, dacă există
        const firstMat = (mod.materials || [])[0];
        if (firstMat) setSelectedMaterialId(firstMat.id);
        if ((prog.percentage || 0) >= 90) {
          setVideoWatched(true);
          setVideoProgress(prog.percentage || 0);
          setQuizPassed(true); // already completed = quiz already passed
          setQuizSubmitted(true);
          // Marchează toate materialele ca parcurse (modul completat)
          if (mod.materials?.length) {
            const allDoneMap = {};
            mod.materials.forEach((m) => {
              const existing = prog.materialsProgress?.[m.id];
              allDoneMap[m.id] = {
                current: existing?.total || existing?.current || 1,
                total: existing?.total || existing?.current || 1,
                completed: true,
              };
            });
            setMaterialsProgress(allDoneMap);
          }
        }
      }
    }
    // Start per-module time counter
    if (timeTickRef.current) clearInterval(timeTickRef.current);
    timeTickRef.current = setInterval(() => {
      if (!training) return;
      const mods = training.course?.modules || [];
      const mod = mods[currentModuleIndex];
      if (!mod) return;
      moduleTimeRef.current[mod.id] = (moduleTimeRef.current[mod.id] || 0) + 1;
      // Forțează re-render ca să se vadă cronometrul mergând (tic la fiecare secundă)
      setDisplayElapsed((e) => e + 1);
    }, 1000);
    return () => {
      if (timeTickRef.current) clearInterval(timeTickRef.current);
    };
  }, [currentModuleIndex, training?.id]);

  const startOrResumeTraining = async () => {
    try {
      const res = await api.post('/trainings/start', { courseId });
      const t = res.data;

      setTraining(t);
      setLocalProgress(t.videoProgress || {});

      const savedIndex = t.currentModuleIndex || 0;
      setCurrentModuleIndex(savedIndex);

      const vp = t.videoProgress || {};
      const modules = t.course?.modules || [];

      if (modules[savedIndex]) {
        const prog = vp[modules[savedIndex].id];
        if (prog) {
          setVideoProgress(prog.percentage || 0);
          if ((prog.percentage || 0) >= 90) setVideoWatched(true);
        }
      }
    } catch (err) {
      console.error('Start training error:', err);
      console.error('Error response:', err.response?.data);

      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 409 && data?.code === 'CROSS_TRAINING_BLOCKED') {
        // Backend a blocat startul (ex: IP nu poate fi început până IIG+ILM sunt complet semnate).
        // Afișăm direct mesajul de la backend — e deja user-friendly.
        showToast(data.error || t('training.errorCannotStart'), 'warning');
      } else if (status === 403) {
        showToast(t('training.errorNotAssigned'), 'error');
      } else if (status === 404) {
        showToast(t('training.errorNotFound'), 'error');
      } else {
        showToast(t('training.errorStarting', { error: data?.error || err.message }), 'error');
      }
      navigate('/my-courses');
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = useCallback(
    async (currentTime, totalTime) => {
      if (!training?.id) return;
      const modules = training.course?.modules || [];
      const currentModule = modules[currentModuleIndex];
      if (!currentModule) return;

      const timeSpent = moduleTimeRef.current[currentModule.id] || 0;
      // Reset so we don't double-count on next call
      moduleTimeRef.current[currentModule.id] = 0;

      try {
        const res = await api.put(`/trainings/${training.id}/progress`, {
          moduleId: currentModule.id,
          currentTime,
          totalTime,
          currentModuleIndex,
          timeSpent,
          visited: true,
        });
        if (res.data?.videoProgress) setLocalProgress(res.data.videoProgress);
      } catch (err) {
        console.error('Save progress error:', err);
      }
    },
    [training, currentModuleIndex]
  );

  // Flush time for text-only modules when navigating
  const flushModuleTime = useCallback(
    async (moduleIdx) => {
      if (!training?.id) return;
      const modules = training.course?.modules || [];
      const mod = modules[moduleIdx];
      if (!mod) return;
      const timeSpent = moduleTimeRef.current[mod.id] || 0;
      moduleTimeRef.current[mod.id] = 0;
      try {
        const res = await api.put(`/trainings/${training.id}/progress`, {
          moduleId: mod.id,
          currentModuleIndex: moduleIdx,
          timeSpent,
          visited: true,
        });
        if (res.data?.videoProgress) setLocalProgress(res.data.videoProgress);
      } catch (err) {
        /* non-blocking */
      }
    },
    [training]
  );

  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = Math.round((v.currentTime / v.duration) * 100);
    setVideoProgress(pct);
    if (pct >= 90) setVideoWatched(true);
  }, []);

  const startTracking = () => {
    if (progressInterval.current) clearInterval(progressInterval.current);
    progressInterval.current = setInterval(() => {
      const v = videoRef.current;
      if (v && !v.paused && v.duration > 0) {
        saveProgress(v.currentTime, v.duration);
      }
    }, 5000);
  };

  const stopTracking = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
  };

  const handleEnded = () => {
    setVideoWatched(true);
    setVideoProgress(100);
    const v = videoRef.current;
    if (v) saveProgress(v.duration, v.duration);
    stopTracking();
  };

  // ---- Loading state ----
  if (loading) {
    return <Spinner size="lg" label={t('training.loading')} />;
  }

  // ---- Training not found state ----
  if (!training) {
    return (
      <div className="text-center py-16">
        <WarningIcon />
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mt-4 mb-2">
          {t('training.notFoundTitle')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {t('training.notFoundDesc')}
        </p>
        <Button variant="primary" onClick={() => navigate('/my-courses')}>
          <ArrowLeftIcon />
          {t('training.backToCourses')}
        </Button>
      </div>
    );
  }

  // ---- Derived data (unchanged logic) ----
  const modules = training.course?.modules || [];
  const currentModule = modules[currentModuleIndex];
  const vp = training.videoProgress || {};
  // A module is done if: text-only (visited/timeSpent>0) OR video watched >=90%
  // localProgress is the live mirror updated after every API save; vp is the initial server snapshot
  // Helper: dat un modul + progresul lui de pe server, returnează dacă toate materialele sale sunt parcurse.
  const allMaterialsCompletedFromServer = (mod, prog) => {
    const mats = mod.materials || [];
    if (mats.length === 0) return true;
    return mats.every((mat) => prog?.materialsProgress?.[mat.id]?.completed === true);
  };

  const completedCount = modules.filter((m, idx) => {
    const prog = { ...(vp[m.id] || {}), ...(localProgress[m.id] || {}) };
    const hasModMatsRaw = (m.materials || []).length > 0;
    if (!m.videoUrl && !hasModMatsRaw) {
      // Being on a text module counts as having visited it
      if (idx === currentModuleIndex) {
        const quizOk = !(m.questions?.length > 0) || quizPassed;
        return quizOk;
      }
      return prog.visited || (prog.timeSpent || 0) > 0;
    }
    // For the current video/materials module also check live state + quiz
    if (idx === currentModuleIndex) {
      const vidOk =
        !m.videoUrl || videoWatched || videoProgress >= 90 || (prog.percentage || 0) >= 90;
      // Materialele curente folosesc state local + ce e pe server.
      // ATENȚIE: NU mai folosim bypass-ul `prog.percentage >= 90` pentru materiale — anterior
      // permitea termiarea cursului dacă progresul general era >= 90% chiar fără să fi vizualizat
      // tot PPTX-ul. Strict gating: fiecare material (PDF / PPTX / Video) trebuie marcat completed.
      const liveAllMatsDone =
        !hasModMatsRaw || m.materials.every((mat) => materialsProgress[mat.id]?.completed === true);
      const matsOk = liveAllMatsDone || allMaterialsCompletedFromServer(m, prog);
      const mediaOk = (!m.videoUrl && !hasModMatsRaw) || (vidOk && matsOk);
      const quizOk = !(m.questions?.length > 0) || quizPassed;
      return mediaOk && quizOk;
    }
    // Module non-curente: ne uităm pe progresul de pe server.
    // Materialele rămân strict — server trebuie să raporteze completed=true pe fiecare.
    const vidOk = !m.videoUrl || (prog.percentage || 0) >= 90;
    const matsOk = allMaterialsCompletedFromServer(m, prog);
    return vidOk && matsOk;
  }).length;
  const overallPct = modules.length > 0 ? Math.round((completedCount / modules.length) * 100) : 0;
  const allDone = completedCount === modules.length;
  const videoUrl = currentModule ? getVideoUrl(currentModule.videoUrl) : null;
  const isEmbedVideo = isEmbed(currentModule?.videoUrl);
  const currentMaterials = currentModule?.materials || [];
  const hasMaterials = currentMaterials.length > 0;
  const hasMedia = !!currentModule?.videoUrl || hasMaterials;
  const hasVideo = !!currentModule?.videoUrl;
  const videoOk = !hasVideo || videoWatched || videoProgress >= 90;
  const allMaterialsDone =
    !hasMaterials || currentMaterials.every((m) => materialsProgress[m.id]?.completed === true);
  const mediaWatched = !hasMedia || (videoOk && allMaterialsDone);
  const selectedMaterial = currentMaterials.find((m) => m.id === selectedMaterialId) || null;
  const moduleQuestions = currentModule?.questions || [];
  const hasModuleQuiz = moduleQuestions.length > 0;
  // Per-module setting for quiz, per-course setting for final test
  const showCorrectAnswersModule = currentModule?.showCorrectAnswers !== false;
  const currentModuleWatched = mediaWatched && (!hasModuleQuiz || quizPassed);

  // Timp minim obligatoriu pe curs — gate activ DOAR dacă minTrainingTime e setat (>0).
  // Dacă e null/0 → fără timer, cursul curge liber.
  const minTimeSec = (training.course?.minTrainingTime || 0) * 60;
  // Gate-ul de timp se aplică DOAR dacă (a) e setat un minim ȘI (b) cursul are conținut (module).
  // Pe un curs gol (0 module) intervalul nu poate atribui timp unui modul inexistent → timer-ul
  // rămânea blocat la infinit. Fără module → nu blocăm.
  const timeGateEnabled = minTimeSec > 0 && modules.length > 0;
  // Timpul total = sumă timpi salvați pe server (localProgress) + secundele neflushate ale modulului curent.
  // displayElapsed e referențiat ca să forțeze recalcularea live (re-render la fiecare secundă).
  const savedTimeSpentSec = Object.values(localProgress).reduce(
    (sum, p) => sum + (p.timeSpent || 0),
    0
  );
  const pendingSessionSec = Object.values(moduleTimeRef.current || {}).reduce(
    (sum, s) => sum + (s || 0),
    0
  );
  const totalTimeSpentSec = savedTimeSpentSec + pendingSessionSec + displayElapsed * 0; // displayElapsed forțează re-render
  const timeOk = !timeGateEnabled || totalTimeSpentSec >= minTimeSec;
  const timeRemainingSec = timeGateEnabled ? Math.max(0, minTimeSec - totalTimeSpentSec) : 0;
  const timeRemainingMin = timeGateEnabled ? Math.ceil(timeRemainingSec / 60) : 0;
  // Format mm:ss pentru cronometrul vizibil
  const fmtClock = (s) => {
    const sec = Math.max(0, Math.floor(s));
    const m = Math.floor(sec / 60);
    const r = sec % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const canProceedToTest = allDone && timeOk;
  const hasFinalTest = !!training.course?.finalTest;

  // Granular progress: completed modules + fraction of current module
  const currentModuleFraction = (() => {
    if (!hasMedia) return 1; // text-only = instant
    if (videoWatched || videoProgress >= 90) return 1;
    if (hasMaterials) {
      // Media fraction: media valorilor (current/total) pentru toate materialele
      const fractions = currentMaterials.map((m) => {
        const p = materialsProgress[m.id];
        if (!p || !p.total) return 0;
        if (p.completed) return 1;
        return Math.min(p.current / p.total, 1);
      });
      return fractions.reduce((a, b) => a + b, 0) / Math.max(fractions.length, 1);
    }
    if (currentModule?.videoUrl) return videoProgress / 100;
    return 0;
  })();
  const granularPct =
    modules.length > 0
      ? Math.round(
          ((completedCount + (completedCount < modules.length ? currentModuleFraction : 0)) /
            modules.length) *
            100
        )
      : 0;

  const courseType = training.course?.type || '';
  const isSU = courseType.endsWith('_SU');
  const serviceLabel = isSU ? t('training.serviceLabelSU') : t('training.serviceLabelSSM');
  const barBg = isSU ? 'bg-danger-600' : 'bg-primary-600';

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      {/* Bară colorată serviciu */}
      <div className={`${barBg} rounded-lg px-4 py-2 flex items-center gap-2`}>
        {isSU ? (
          <svg
            className="w-4 h-4 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        )}
        <span className="text-white text-sm font-medium">{serviceLabel}</span>
      </div>

      {/* Compact header: back + course title.
          Buton „Ieși din instruire" — deschide ConfirmModal vizual cu opțiunea de a pierde
          progresul. Pe confirmare, ștergem training-ul (force discard) și navigăm. */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setShowExitModal(true)}>
          <ArrowLeftIcon />
          {t('training.exitTraining')}
        </Button>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate">
          {training.course?.title}
        </h1>
        <div className="w-24" />
      </div>

      {/* Cronometru timp minim pe curs — vizibil DOAR când minTrainingTime e setat */}
      {timeGateEnabled && (
        <Card
          padding={false}
          className={`px-4 py-3 ${timeOk ? 'border-l-4 border-accent-500' : 'border-l-4 border-warning-500'}`}
        >
          <div className="flex items-center gap-3">
            <svg
              className={`w-5 h-5 flex-shrink-0 ${timeOk ? 'text-accent-600 dark:text-accent-400' : 'text-warning-600 dark:text-warning-400'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1 min-w-0">
              {timeOk ? (
                <p className="text-sm font-medium text-accent-700 dark:text-accent-300">
                  {t('training.timerDoneMin', { min: fmtClock(minTimeSec) })}
                </p>
              ) : (
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('training.timerRemaining', { remaining: fmtClock(timeRemainingSec) })}
                </p>
              )}
              <p className="text-xs text-slate-500 dark:text-slate-400 tabular-nums mt-0.5">
                {t('training.timerOnCourse', {
                  elapsed: fmtClock(totalTimeSpentSec),
                  min: fmtClock(minTimeSec),
                })}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Overall progress — modules completed */}
      {modules.length > 0 && (
        <Card padding={false} className="px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('training.sectionsCompleted', {
                completed: completedCount,
                total: modules.length,
              })}
            </span>
            <span className="text-sm font-semibold text-primary-700 dark:text-primary-300 tabular-nums">
              {overallPct}%
            </span>
          </div>
          <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary-600 h-full rounded-full transition-all duration-300"
              style={{ width: `${overallPct}%` }}
            />
          </div>
        </Card>
      )}

      {/* Module navigator + progress */}
      <Card padding={false}>
        <div className="px-4 py-3 flex items-center gap-3">
          {/* Prev button */}
          <button
            onClick={async () => {
              await flushModuleTime(currentModuleIndex);
              setCurrentModuleIndex((p) => p - 1);
            }}
            disabled={currentModuleIndex <= 0}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            title={t('training.prevMaterial')}
            aria-label={t('training.prevMaterial')}
          >
            <svg
              className="w-5 h-5 text-slate-600 dark:text-slate-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Module info — center */}
          <div className="flex-1 min-w-0 text-center">
            <div className="flex items-center justify-center gap-2">
              {/* Module step indicators */}
              {modules.map((mod, idx) => {
                const modProg = { ...(vp[mod.id] || {}), ...(localProgress[mod.id] || {}) };
                const hasModMats = (mod.materials || []).length > 0;
                const hasModMedia = !!mod.videoUrl || hasModMats;
                const matsAllDone =
                  !hasModMats ||
                  mod.materials.every(
                    (mat) => modProg.materialsProgress?.[mat.id]?.completed === true
                  );
                const vidAllDone = !mod.videoUrl || (modProg.percentage || 0) >= 90;
                const done = !hasModMedia
                  ? idx === currentModuleIndex || modProg.visited || (modProg.timeSpent || 0) > 0
                  : (vidAllDone && matsAllDone) || (idx === currentModuleIndex && mediaWatched);
                const isCurr = idx === currentModuleIndex;
                return (
                  <button
                    key={mod.id}
                    onClick={() => setCurrentModuleIndex(idx)}
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      done
                        ? 'bg-accent-100 dark:bg-accent-950/50 text-accent-700 dark:text-accent-300'
                        : isCurr
                          ? 'bg-primary-600 text-white ring-2 ring-primary-200 dark:ring-primary-700'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                    title={mod.title}
                  >
                    {done ? (
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      idx + 1
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-1.5 truncate">
              {t('training.sectionOf', {
                current: currentModuleIndex + 1,
                total: modules.length,
                title: currentModule?.title,
              })}
            </p>
          </div>

          {/* Next button */}
          <button
            onClick={async () => {
              await flushModuleTime(currentModuleIndex);
              setCurrentModuleIndex((p) => p + 1);
            }}
            disabled={currentModuleIndex >= modules.length - 1 || !currentModuleWatched}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            title={
              !currentModuleWatched
                ? t('training.completeCurrentMaterial')
                : t('training.nextMaterial')
            }
            aria-label={t('training.nextMaterial')}
          >
            <svg
              className="w-5 h-5 text-slate-600 dark:text-slate-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        {/* Granular progress bar */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${granularPct >= 100 ? 'bg-accent-500' : 'bg-primary-600'}`}
                style={{ width: `${Math.min(granularPct, 100)}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 tabular-nums whitespace-nowrap">
              {granularPct}%
            </span>
          </div>
        </div>
        {/* #6: explică vizibil de ce e gri butonul „mai departe" (nu doar tooltip ascuns).
            Listează materialele care nu sunt parcurse complet (PDF/PPTX/Video) ca să știi
            exact ce mai ai de făcut. */}
        {!currentModuleWatched &&
          (() => {
            const pending = currentMaterials.filter(
              (m) => !(materialsProgress[m.id]?.completed === true)
            );
            return (
              <div className="px-4 pb-3 -mt-1 space-y-1">
                <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                    />
                  </svg>
                  {t('training.pendingMaterialsHint')}
                </p>
                {pending.length > 0 && (
                  <ul className="text-[11px] text-amber-700 dark:text-amber-300 ml-5 list-disc">
                    {pending.map((p) => {
                      const prog = materialsProgress[p.id] || { current: 0, total: 0 };
                      const unit =
                        p.kind === 'VIDEO'
                          ? ''
                          : p.kind === 'POWERPOINT'
                            ? ` ${t('training.unitSlides')}`
                            : ` ${t('training.unitPages')}`;
                      return (
                        <li key={p.id}>
                          {p.originalName}
                          {p.kind === 'VIDEO'
                            ? ` (${t('training.materialNotFullyWatched')})`
                            : prog.total > 0
                              ? ` (${prog.current}/${prog.total}${unit})`
                              : ` (${t('training.materialNotOpened')})`}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })()}
      </Card>

      {/* Main content area — full width */}
      <div className="space-y-4">
        {/* Video section */}
        {currentModule?.videoUrl && (
          <Card padding={false} className="overflow-hidden">
            {videoError ? (
              <div className="p-10 text-center">
                <div className="flex justify-center mb-4">
                  <VideoCameraOffIcon />
                </div>
                <p className="text-slate-700 dark:text-slate-300 font-medium mb-1">
                  {t('training.videoUnavailable')}
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-400 font-mono mb-6 break-all">
                  {currentModule.videoUrl}
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setVideoError(false);
                      videoRef.current?.load();
                    }}
                  >
                    <RefreshIcon />
                    {t('training.retryVideo')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setVideoWatched(true)}>
                    <EyeIcon />
                    {t('training.markAsWatched')}
                  </Button>
                </div>
              </div>
            ) : isEmbedVideo ? (
              <div>
                <div className="aspect-video">
                  <iframe
                    src={videoUrl}
                    title={currentModule.title}
                    className="w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                </div>
                <div className="px-4 py-3 bg-warning-50 dark:bg-warning-950/50 border-t border-warning-200 dark:border-warning-700 flex items-center justify-between gap-3">
                  <p className="text-xs text-warning-700 dark:text-warning-300">
                    {t('training.watchFullVideoThenMark')}
                  </p>
                  <Button
                    variant="accent"
                    size="sm"
                    onClick={async () => {
                      setVideoWatched(true);
                      if (training?.id && currentModule) {
                        try {
                          const res = await api.put(`/trainings/${training.id}/progress`, {
                            moduleId: currentModule.id,
                            currentTime: 100,
                            totalTime: 100,
                            currentModuleIndex,
                            timeSpent: moduleTimeRef.current[currentModule.id] || 0,
                            visited: true,
                          });
                          moduleTimeRef.current[currentModule.id] = 0;
                          if (res.data?.videoProgress) setLocalProgress(res.data.videoProgress);
                        } catch {
                          /* non-blocking */
                        }
                      }
                    }}
                    className="flex-shrink-0"
                  >
                    <CheckIcon />
                    {t('training.watchedVideo')}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="aspect-video bg-black">
                  <SecureVideo
                    ref={videoRef}
                    path={currentModule.videoUrl}
                    controls
                    className="w-full h-full"
                    onError={() => setVideoError(true)}
                    onLoadedData={() => setVideoError(false)}
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={startTracking}
                    onPause={stopTracking}
                    onEnded={handleEnded}
                    preload="metadata"
                  />
                </div>
                <div className="px-4 py-3 bg-surface-secondary border-t border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          videoProgress >= 90 ? 'bg-accent-500' : 'bg-primary-600'
                        }`}
                        style={{ width: `${videoProgress}%` }}
                      />
                    </div>
                    {videoProgress >= 90 ? (
                      <Badge color="green">
                        <span className="flex items-center gap-1">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          {t('training.watched')}
                        </span>
                      </Badge>
                    ) : (
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 tabular-nums flex-shrink-0">
                        {videoProgress}%
                      </span>
                    )}
                  </div>
                  {videoProgress < 90 && (
                    <p className="text-xs text-slate-400 dark:text-slate-400 mt-1.5">
                      {t('training.watchAtLeast90')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Materials section — meniu materiale + viewer dedesubt */}
        {hasMaterials && (
          <Card padding={false} className="overflow-hidden">
            <div className="p-3 border-b border-slate-200 dark:border-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-primary-600 dark:text-primary-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                {t('training.moduleMaterials', { count: currentMaterials.length })}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('training.mustCompleteAllMaterials')}
              </p>
            </div>

            {/* Sidebar materiale (stânga) + viewer (dreapta) */}
            <div className="flex flex-col md:flex-row md:items-stretch">
              {/* Bară verticală de toggle pe stânga (desktop only) */}
              <button
                type="button"
                onClick={() => setMaterialsSidebarOpen((o) => !o)}
                className="hidden md:flex items-center justify-center w-5 self-stretch border-r border-slate-200 dark:border-slate-800 bg-slate-50 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-700 transition-colors group"
                title={
                  materialsSidebarOpen
                    ? t('training.hideMaterialsList')
                    : t('training.showMaterialsList')
                }
                aria-label={
                  materialsSidebarOpen
                    ? t('training.hideMaterialsList')
                    : t('training.showMaterialsList')
                }
              >
                <svg
                  className={`w-3.5 h-3.5 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200 transition-transform ${materialsSidebarOpen ? '' : 'rotate-180'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              {/* Lista de materiale — sidebar cu scroll propriu (colapsabil) */}
              {materialsSidebarOpen && (
                <div className="md:w-72 md:flex-shrink-0 md:border-r border-slate-200 dark:border-slate-800 max-h-64 md:max-h-[640px] overflow-y-auto">
                  <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                    {currentMaterials.map((mat, idx) => {
                      const p = materialsProgress[mat.id] || {
                        current: 0,
                        total: 0,
                        completed: false,
                      };
                      const isSelected = mat.id === selectedMaterialId;
                      const isPpt = mat.kind === 'POWERPOINT';
                      const isVideo = mat.kind === 'VIDEO';
                      const pageLabel = isPpt
                        ? t('training.unitSlides')
                        : isVideo
                          ? ''
                          : t('training.unitPages');
                      const typeLabel = isVideo ? 'Video' : isPpt ? 'PowerPoint' : 'PDF';
                      return (
                        <li key={mat.id}>
                          <button
                            type="button"
                            onClick={() => setSelectedMaterialId(mat.id)}
                            className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                              isSelected
                                ? 'bg-primary-50 dark:bg-primary-950/30 border-l-2 border-primary-600 dark:border-primary-400'
                                : 'border-l-2 border-transparent hover:bg-slate-50 dark:hover:bg-slate-800'
                            }`}
                          >
                            {/* Indicator stare */}
                            <span
                              className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold ${
                                p.completed
                                  ? 'bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-300'
                                  : isSelected
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                              }`}
                            >
                              {p.completed ? (
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={3}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              ) : (
                                idx + 1
                              )}
                            </span>
                            {/* Icŏnă tip */}
                            <svg
                              className={`w-4 h-4 flex-shrink-0 ${isVideo ? 'text-violet-500' : isPpt ? 'text-orange-500' : 'text-red-500'}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d={
                                  isVideo
                                    ? 'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z'
                                    : isPpt
                                      ? 'M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 21h16.5M16.5 3.75h.008v.008H16.5V3.75z'
                                      : 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'
                                }
                              />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                                {mat.originalName}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {typeLabel}
                                {!isVideo &&
                                  p.total > 0 &&
                                  ` • ${p.current}/${p.total} ${pageLabel}`}
                                {p.completed && ` • ${t('training.materialCompleted')}`}
                              </p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Viewer pentru materialul selectat (dreapta) */}
              <div className="flex-1 min-w-0 p-3 border-t md:border-t-0 border-slate-200 dark:border-slate-800">
                {selectedMaterial ? (
                  <div className="space-y-3">
                    {selectedMaterial.kind === 'POWERPOINT' ? (
                      <PowerPointViewer
                        url={selectedMaterial.fileUrl}
                        onProgress={({ current, total }) => {
                          setMaterialsProgress((prev) => {
                            const existing = prev[selectedMaterial.id] || {};
                            const completed =
                              existing.completed === true || (current >= total && total > 0);
                            const updated = {
                              ...prev,
                              [selectedMaterial.id]: { current, total, completed },
                            };
                            if (training?.id && currentModule) {
                              api
                                .put(`/trainings/${training.id}/progress`, {
                                  moduleId: currentModule.id,
                                  currentModuleIndex,
                                  materialsProgress: {
                                    [selectedMaterial.id]: { current, total, completed },
                                  },
                                })
                                .catch(() => {});
                            }
                            return updated;
                          });
                        }}
                      />
                    ) : selectedMaterial.kind === 'VIDEO' ? (
                      // Video material — <video> protejat (URL semnat prin /media/sign). Marcăm
                      // „parcurs" la `ended`. fileUrl e relativ (/uploads/videos/...).
                      <SecureVideo
                        key={selectedMaterial.id}
                        path={selectedMaterial.fileUrl}
                        controls
                        className="w-full max-h-[640px] rounded-lg bg-black"
                        fallbackClassName="w-full aspect-video rounded-lg"
                        onEnded={() => {
                          setMaterialsProgress((prev) => {
                            const updated = {
                              ...prev,
                              [selectedMaterial.id]: { current: 1, total: 1, completed: true },
                            };
                            if (training?.id && currentModule) {
                              api
                                .put(`/trainings/${training.id}/progress`, {
                                  moduleId: currentModule.id,
                                  currentModuleIndex,
                                  materialsProgress: {
                                    [selectedMaterial.id]: {
                                      current: 1,
                                      total: 1,
                                      completed: true,
                                    },
                                  },
                                })
                                .catch(() => {});
                            }
                            return updated;
                          });
                        }}
                      />
                    ) : (
                      <PdfViewer
                        url={selectedMaterial.fileUrl}
                        onProgress={({ current, total }) => {
                          setMaterialsProgress((prev) => {
                            const existing = prev[selectedMaterial.id] || {};
                            const completed =
                              existing.completed === true || (current >= total && total > 0);
                            const updated = {
                              ...prev,
                              [selectedMaterial.id]: { current, total, completed },
                            };
                            if (training?.id && currentModule) {
                              api
                                .put(`/trainings/${training.id}/progress`, {
                                  moduleId: currentModule.id,
                                  currentModuleIndex,
                                  materialsProgress: {
                                    [selectedMaterial.id]: { current, total, completed },
                                  },
                                })
                                .catch(() => {});
                            }
                            return updated;
                          });
                        }}
                      />
                    )}
                    {/* Descrierea/notă text afișată sub material — set în editor de SSM-ist. */}
                    {selectedMaterial.description && (
                      <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                        {selectedMaterial.description}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center text-slate-500 dark:text-slate-400 py-12">
                    <svg
                      className="w-10 h-10 mb-2 opacity-50"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-sm">{t('training.chooseMaterial')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Confirm bar — activă doar când toate materialele sunt parcurse */}
            <div className="px-4 py-3 bg-surface-secondary border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3">
              {allMaterialsDone ? (
                videoWatched || videoProgress >= 90 || !hasVideo ? (
                  <Badge color="green">
                    <span className="flex items-center gap-1">
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {t('training.allMaterialsCompleted')}
                    </span>
                  </Badge>
                ) : (
                  <>
                    <p className="text-xs text-green-600 dark:text-green-300 font-medium">
                      {t('training.allMaterialsWatchVideo')}
                    </p>
                  </>
                )
              ) : (
                <p className="text-xs text-amber-600 dark:text-amber-300">
                  {(() => {
                    const remaining = currentMaterials.filter(
                      (m) => !materialsProgress[m.id]?.completed
                    ).length;
                    return t('training.remainingMaterials', { count: remaining });
                  })()}
                </p>
              )}
            </div>
          </Card>
        )}

        {/* Module content */}
        {currentModule?.content && (
          <Card>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
              {currentModule.title}
            </h3>
            <div
              className="prose prose-sm max-w-none text-slate-700 dark:text-slate-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentModule.content) }}
            />
          </Card>
        )}

        {/* Module quiz — checkpoint questions */}
        {hasModuleQuiz &&
          mediaWatched &&
          (() => {
            // Build shuffled answers on first render for this module
            const getShuffledAnswers = (questionId, answers) => {
              if (shuffledAnswers[questionId]) return shuffledAnswers[questionId];
              const shuffled = shuffleArray(answers || []);
              // Side effect in render — safe because it's idempotent
              setShuffledAnswers((prev) => ({ ...prev, [questionId]: shuffled }));
              return shuffled;
            };

            return (
              <Card>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-primary-500 dark:text-primary-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t('training.quizTitle')}
                  {quizPassed && (
                    <Badge color="green">
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {t('training.quizPassed')}
                      </span>
                    </Badge>
                  )}
                </h3>

                <div className="space-y-4">
                  {moduleQuestions.map((q, qIdx) => {
                    const selectedAnswer = quizAnswers[q.id];
                    const correctAnswer = q.answers?.find((a) => a.isCorrect);
                    const isAnswered = selectedAnswer !== undefined;
                    const isCorrect = isAnswered && selectedAnswer === correctAnswer?.id;
                    const showResult = quizSubmitted;
                    const displayAnswers = getShuffledAnswers(q.id, q.answers);

                    return (
                      <div
                        key={q.id}
                        className={`p-4 rounded-lg border ${showResult ? (isCorrect ? 'border-green-200 dark:border-slate-700 bg-green-50 dark:bg-slate-800/60' : 'border-red-200 dark:border-slate-700 bg-red-50 dark:bg-slate-800/60') : 'border-slate-200 dark:border-slate-800'}`}
                      >
                        <p className="font-medium text-slate-900 dark:text-slate-100 mb-3">
                          {qIdx + 1}. {q.text}
                        </p>
                        <div className="space-y-2">
                          {displayAnswers.map((a) => {
                            const isSelected = selectedAnswer === a.id;
                            const isCorrectAnswer = a.isCorrect;
                            return (
                              <label
                                key={a.id}
                                className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                                  quizSubmitted
                                    ? showCorrectAnswersModule && isCorrectAnswer
                                      ? 'border-green-400 dark:border-slate-700 bg-green-50 dark:bg-slate-800/60'
                                      : isSelected && !isCorrectAnswer
                                        ? 'border-red-400 dark:border-slate-700 bg-red-50 dark:bg-slate-800/60'
                                        : isSelected && isCorrectAnswer
                                          ? 'border-green-400 dark:border-slate-700 bg-green-50 dark:bg-slate-800/60'
                                          : 'border-slate-200 dark:border-slate-800'
                                    : isSelected
                                      ? 'border-primary-400 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50 cursor-pointer'
                                      : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`quiz-${q.id}`}
                                  value={a.id}
                                  aria-label={a.text}
                                  checked={isSelected}
                                  onChange={() => {
                                    if (!quizSubmitted)
                                      setQuizAnswers((prev) => ({ ...prev, [q.id]: a.id }));
                                  }}
                                  disabled={quizSubmitted}
                                  className="w-4 h-4 text-primary-600"
                                />
                                <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">
                                  {a.text}
                                </span>
                                {showResult && showCorrectAnswersModule && isCorrectAnswer && (
                                  <svg
                                    className="w-4 h-4 text-green-600 dark:text-green-300 flex-shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                )}
                                {showResult && isSelected && !isCorrectAnswer && (
                                  <svg
                                    className="w-4 h-4 text-red-500 dark:text-red-300 flex-shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!quizSubmitted ? (
                  <Button
                    variant="primary"
                    className="mt-4"
                    disabled={Object.keys(quizAnswers).length < moduleQuestions.length}
                    onClick={() => {
                      setQuizSubmitted(true);
                      const allCorrect = moduleQuestions.every((q) => {
                        const correct = q.answers?.find((a) => a.isCorrect);
                        return quizAnswers[q.id] === correct?.id;
                      });
                      if (allCorrect) {
                        setQuizPassed(true);
                      }
                    }}
                  >
                    {t('training.quizCheck')}
                  </Button>
                ) : !quizPassed ? (
                  <div className="mt-4 space-y-3">
                    <Alert type="warning">
                      <span className="text-sm">{t('training.quizWrong')}</span>
                    </Alert>
                    <div className="flex gap-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setQuizAnswers({});
                          setQuizSubmitted(false);
                          // Re-shuffle answers for new attempt
                          setShuffledAnswers({});
                        }}
                      >
                        {t('training.quizRetryPlain')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Alert type="success" className="mt-4">
                    <span className="text-sm font-medium">{t('training.quizAllCorrect')}</span>
                  </Alert>
                )}
              </Card>
            );
          })()}

        {/* Action area: test / finalize / time warning */}
        {allDone && !timeOk && (
          <Alert type="warning">
            <span className="text-sm">
              <Trans
                i18nKey="training.waitTimeRemaining"
                values={{ minutes: timeRemainingMin }}
                components={{ b: <strong /> }}
              />
            </span>
          </Alert>
        )}

        {allDone && canProceedToTest && (
          <Card className="text-center">
            <div className="py-2">
              <p className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                {t('training.allDone')}
              </p>
              {hasFinalTest ? (
                fromTestId ? (
                  <>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                      {t('training.reviewedBackToTest')}
                    </p>
                    <Button
                      variant="accent"
                      size="lg"
                      onClick={() => navigate(`/training/${fromTestId}/test`)}
                    >
                      <ClipboardDocumentCheckIcon />
                      {t('training.backToTest')}
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                      {t('training.signThenTest')}
                    </p>
                    <Button
                      variant="accent"
                      size="lg"
                      onClick={async () => {
                        await flushModuleTime(currentModuleIndex);
                        navigate(`/training/${training.id}/sign`);
                      }}
                    >
                      <FinalizeIcon />
                      {t('training.signAndContinue')}
                    </Button>
                  </>
                )
              ) : (
                <>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                    {t('training.canFinishTraining')}
                  </p>
                  <Button
                    variant="accent"
                    size="lg"
                    onClick={async () => {
                      await flushModuleTime(currentModuleIndex);
                      navigate(`/training/${training.id}/sign`);
                    }}
                  >
                    <FinalizeIcon />
                    {t('training.finishAndSign')}
                  </Button>
                </>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Modal de ieșire — pe confirmare pierdem progresul (DELETE training force). */}
      <ConfirmModal
        open={showExitModal}
        onClose={() => !exitLoading && setShowExitModal(false)}
        onConfirm={async () => {
          if (!training?.id) {
            setShowExitModal(false);
            navigate('/my-courses');
            return;
          }
          setExitLoading(true);
          try {
            const res = await api.post(`/trainings/${training.id}/discard-if-empty`, {
              force: true,
            });
            if (res.data?.discarded) {
              showToast(t('training.exitDeletedSuccess'), 'success');
            } else {
              showToast(t('training.exitCannotDelete'), 'warning');
            }
          } catch (err) {
            console.error(
              'Exit training error:',
              err.response?.status,
              err.response?.data || err.message
            );
            showToast(t('training.exitServerError'), 'error');
          } finally {
            setExitLoading(false);
            setShowExitModal(false);
            navigate('/my-courses');
          }
        }}
        title={t('training.exitModalTitle')}
        message={t('training.exitModalMessage')}
        confirmLabel={exitLoading ? t('training.exitDeleting') : t('training.exitConfirm')}
        cancelLabel={t('training.exitCancel')}
        variant="danger"
        loading={exitLoading}
      />
    </div>
  );
};

export default TrainingPage;
